import type { ControlBarPort } from "./ControlBarPort";
import type { SpinCoordinator } from "../../spin/SpinCoordinator";
import { multiplyMinorByMultiplier } from "../../money/multiplyMinorByMultiplier";
import type { BetSystem } from "../../bet/BetSystemTypes";
import type { FeatureStateStore } from "../../feature-state/FeatureStateStore";
import type { FeatureModeStore } from "../../feature-mode/FeatureModeStore";
import type { AutoSpinStore } from "../../autospin/AutoSpinStore";
import type { MoneyFormatter } from "../../money/MoneyFormatter";
import type { ViewportStore } from "@slot/presentation";

/** What the bar's rules read. */
export interface ControlBarPresenterDeps {
  betSystem: BetSystem;
  featureStateStore: FeatureStateStore;
  featureModeStore: FeatureModeStore;
  autoSpinStore: AutoSpinStore;
  formatter: MoneyFormatter;
  viewport: ViewportStore;
  /** Feature-mode bet multipliers by mode id (the displayed bet in the change popup). */
  featureModeMultipliersById: Readonly<Record<string, number>>;
  /** A round waiting for the player's Spin (decision 55): the Spin button's face follows it. */
  playerSpins?: { subscribe(listener: () => void): () => void };
}

interface Lifecycle {
  start(): void | Promise<void>;
  stop(): void;
}
interface Renders {
  render(): void;
}
/** A screen-level overlay the bar closes before another opens. */
interface Overlay {
  isOpen(): boolean;
  close(): void;
}
interface Closable {
  closeIfOpen(): void;
}

/** The presenters over the bar's ports, built by the composition root (`createControlBar`). */
export interface ControlBarPresenters {
  bet: Renders;
  spinButton: Renders;
  spinInput: Lifecycle;
  featureModeUi: Lifecycle;
  header: Lifecycle | null;
  menu: Lifecycle & Renders & Overlay;
  infoPage: (Lifecycle & Overlay) | null;
  gameHistory: (Lifecycle & Overlay) | null;
  featurePurchase: (Lifecycle & Renders & Closable) | null;
  autoSpin: Lifecycle & Renders & Closable;
  speedMode: Lifecycle & Renders & Closable;
}

/**
 * The control bar's rules over the presenters it spans: their lifecycle and re-render fan-out, one
 * overlay at a time, the mobile bet-change popup, the bet controls' visibility.
 */
export class ControlBarPresenter {
  private readonly deps: ControlBarPresenterDeps;
  private readonly bar: ControlBarPort;
  private readonly spinCoordinator: SpinCoordinator;
  private readonly presenters: ControlBarPresenters;
  /** Replay mode: the spin panel stays hidden — its presenter would re-show it on the next feature-state render. */
  private readonly replayMode: boolean;
  private unsubscribe: Array<() => void> = [];
  private lastBetMinor: string | null = null;

  public constructor(input: { bar: ControlBarPort; spinCoordinator: SpinCoordinator; presenters: ControlBarPresenters; deps: ControlBarPresenterDeps; replayMode: boolean }) {
    this.bar = input.bar;
    this.spinCoordinator = input.spinCoordinator;
    this.presenters = input.presenters;
    this.deps = input.deps;
    this.replayMode = input.replayMode;
  }

  public async start(): Promise<void> {
    const p = this.presenters;
    p.infoPage?.start();
    p.gameHistory?.start();
    p.menu.start();
    p.featureModeUi.start();
    p.header?.start();
    await p.featurePurchase?.start();
    p.autoSpin.start();
    p.speedMode.start();
    p.spinInput.start();
    this.bar.betChangePopup.setOnHidden(() => this.bar.betPanel.setBetValueVisible(true));
    this.render();
    this.unsubscribe = [
      this.deps.betSystem.subscribe((snapshot) => {
        const previousBetMinor = this.lastBetMinor;
        this.lastBetMinor = snapshot.currentBetMinor;
        this.render();
        if (previousBetMinor === null || previousBetMinor === snapshot.currentBetMinor) return;
        this.showBetChangePopup();
      }),
      this.spinCoordinator.subscribe(() => this.render()),
      this.deps.featureModeStore.subscribe(() => this.render()),
      this.deps.autoSpinStore.subscribe(() => this.render()),
      this.deps.featureStateStore.subscribe(() => this.render()),
      this.deps.viewport.subscribe(() => this.render()),
      ...(this.deps.playerSpins ? [this.deps.playerSpins.subscribe(() => this.render())] : []),
    ];
  }

  public stop(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe = [];
    const p = this.presenters;
    p.header?.stop();
    p.featurePurchase?.stop();
    p.autoSpin.stop();
    p.speedMode.stop();
    p.spinInput.stop();
    this.bar.betChangePopup.hideImmediate(false);
    this.bar.betPanel.setBetValueVisible(true);
    p.featureModeUi.stop();
    p.menu.stop();
    p.infoPage?.stop();
    p.gameHistory?.stop();
  }

  public render(): void {
    const p = this.presenters;
    p.bet.render();
    // Not in replay mode: `SpinButtonPresenter.render` sets the panel visible on every pass.
    if (!this.replayMode) p.spinButton.render();
    p.menu.render();
    p.featurePurchase?.render();
    p.autoSpin.render();
    p.speedMode.render();
    this.renderBetControlsVisibility();
  }

  /** Before a control opens its overlay: every other overlay closes. */
  public closeOverlays(): void {
    this.closeOverlaysExceptFeaturePurchase();
    this.presenters.featurePurchase?.closeIfOpen();
  }

  /** Before a bet change: the feature purchase panel stays (its top bar carries the same bet controls). */
  public closeOverlaysExceptFeaturePurchase(): void {
    const p = this.presenters;
    if (p.menu.isOpen()) p.menu.close();
    if (p.infoPage?.isOpen()) p.infoPage.close();
    if (p.gameHistory?.isOpen()) p.gameHistory.close();
    p.autoSpin.closeIfOpen();
    p.speedMode.closeIfOpen();
  }

  /** Mobile only, outside a feature: the new bet in a popup for a second; elsewhere the popup is dropped at once. The amount stays shown. */
  private showBetChangePopup(): void {
    const mobile = this.deps.viewport.getSnapshot().shape !== "desktop";
    if (this.deps.featureStateStore.isFeatureActive() || !mobile) {
      this.bar.betChangePopup.hideImmediate(false);
      this.bar.betPanel.setBetValueVisible(true);
      return;
    }
    this.bar.betChangePopup.show(this.getDisplayedBetText());
  }

  private renderBetControlsVisibility(): void {
    // A replay is playback: the bar's lockdown hides the bet controls once, and no later render may
    // bring them back (finding 10, 2026-09-15 — the desktop rule ignores the locked bet).
    if (this.replayMode) {
      this.bar.betPanel.setControlsVisible(false);
      return;
    }
    const freeSpinsActive = this.deps.featureStateStore.isFeatureActive();
    const desktop = this.deps.viewport.getSnapshot().shape === "desktop";
    const betChangeLocked = !this.spinCoordinator.getSnapshot().canChangeBet;
    const autoSpinRunning = this.deps.autoSpinStore.getSnapshot().running;
    const hideBetControls = freeSpinsActive || (!desktop && betChangeLocked) || (desktop && autoSpinRunning);
    this.bar.betPanel.setControlsVisible(!hideBetControls);
  }

  private getDisplayedBetText(): string {
    const baseBetMinor = this.deps.betSystem.getCurrentBetMinor();
    const activeFeatureMode = this.deps.featureModeStore.getSnapshot().activeMode;
    const multiplier = activeFeatureMode ? this.deps.featureModeMultipliersById[activeFeatureMode.id] : undefined;
    const displayedBetMinor = typeof multiplier === "number" && Number.isFinite(multiplier) && multiplier > 0 ? multiplyMinorByMultiplier(baseBetMinor, multiplier) : baseBetMinor;
    return this.deps.formatter.formatMinor(displayedBetMinor);
  }
}
